import {
  type ChannelThreadId,
  type DraftMessageId,
  DraftMessageId as DraftMessageIdSchema,
  type MessageId,
  type SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  type MongoUserId,
  MongoUserId as MongoUserIdSchema,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type { SaveDraftInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { supportTicketCountsQueryKey } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import { toast } from "@redotech/redo-web/alert";
import { and, eq, isUndefined, useLiveQuery } from "@tanstack/react-db";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "effect";
import { useCallback, useContext, useRef, useState } from "react";
import type { DraftCollectionItem } from "../db/collections";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

interface UseDraftOptions {
  supportTicketId: SupportTicketId;
  threadId: ChannelThreadId;
  inReplyToMessageId?: MessageId;
  enabled?: boolean;
}

/**
 * Hook to fetch the current draft for a support ticket thread context.
 * Uses the TanStack DB drafts collection instead of React Query directly.
 */
export function useDraft({
  supportTicketId,
  threadId,
  inReplyToMessageId,
}: UseDraftOptions) {
  const collections = useGlobalSupportTicketCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ draft: collections.drafts })
        .where(({ draft }) =>
          inReplyToMessageId
            ? and(
                eq(draft.supportTicketId, String(supportTicketId)),
                eq(draft.threadId, String(threadId)),
                eq(draft.inReplyToMessageId, String(inReplyToMessageId)),
                eq(draft.kind, "merchant"),
              )
            : and(
                eq(draft.supportTicketId, String(supportTicketId)),
                eq(draft.threadId, String(threadId)),
                isUndefined(draft.inReplyToMessageId),
                eq(draft.kind, "merchant"),
              ),
        )
        .findOne(),
    [supportTicketId, threadId, inReplyToMessageId],
  );

  return { data: result.data ?? null };
}

export function useAiCopilotDraft({
  supportTicketId,
  threadId,
  inReplyToMessageId,
}: UseDraftOptions) {
  const collections = useGlobalSupportTicketCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ draft: collections.drafts })
        .where(({ draft }) =>
          inReplyToMessageId
            ? and(
                eq(draft.supportTicketId, String(supportTicketId)),
                eq(draft.threadId, String(threadId)),
                eq(draft.inReplyToMessageId, String(inReplyToMessageId)),
                eq(draft.kind, "ai_copilot"),
                eq(draft.candidateState, "visible"),
              )
            : and(
                eq(draft.supportTicketId, String(supportTicketId)),
                eq(draft.threadId, String(threadId)),
                isUndefined(draft.inReplyToMessageId),
                eq(draft.kind, "ai_copilot"),
                eq(draft.candidateState, "visible"),
              ),
        )
        .findOne(),
    [supportTicketId, threadId, inReplyToMessageId],
  );

  return { data: result.data ?? null };
}

interface UseDraftMutationsOptions {
  supportTicketId: SupportTicketId;
  threadId: ChannelThreadId;
  inReplyToMessageId?: MessageId;
  existingDraftId?: DraftMessageId | null;
  editorMode: "email" | "chat";
}

/**
 * Builds a temp DraftCollectionItem for non-optimistic inserts.
 * The item is never displayed — it only carries fields needed by onInsert
 * to make the saveDraft API call. Placeholder values are used for fields
 * that only exist on server-returned drafts (id, timestamps).
 */
function buildTempDraftItem(
  supportTicketId: SupportTicketId,
  threadId: ChannelThreadId,
  senderMongoUserId: MongoUserId,
  inReplyToMessageId: MessageId | undefined,
  overrides: Partial<
    Pick<
      DraftCollectionItem,
      "content" | "plainContent" | "attachments" | "channel" | "channelData"
    >
  >,
): DraftCollectionItem {
  const now = DateTime.unsafeMake(new Date());
  return {
    id: DraftMessageIdSchema.generate(),
    supportTicketId,
    threadId,
    inReplyToMessageId: inReplyToMessageId
      ? String(inReplyToMessageId)
      : undefined,
    content:
      overrides.content ??
      JSON.stringify([{ type: "p", children: [{ text: "" }] }]),
    plainContent: overrides.plainContent ?? null,
    attachments: overrides.attachments ?? [],
    senderMongoUserId,
    senderType: "merchant",
    channel: overrides.channel ?? "redoChat",
    channelData: overrides.channelData ?? null,
    status: "PENDING",
    kind: "merchant",
    candidateState: null,
    version: 1,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Hook to manage draft mutations using the TanStack DB collection's
 * onInsert / onUpdate / onDelete callbacks (defined in collections.ts).
 */
export function useDraftMutations({
  supportTicketId,
  threadId,
  inReplyToMessageId,
  existingDraftId,
  editorMode,
}: UseDraftMutationsOptions) {
  const collections = useGlobalSupportTicketCollections();
  const queryClient = useQueryClient();
  const user = useContext(UserContext);
  const senderMongoUserId = user
    ? MongoUserIdSchema.fromString(user._id)
    : MongoUserIdSchema.makeNew();
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const invalidateSupportTicketCounts = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: supportTicketCountsQueryKey,
    });
  }, [queryClient]);

  const incrementSupportTicketDrafts = useCallback(
    (delta: number) => {
      const supportTicket =
        collections.supportTicketsList.collection.get(supportTicketId);
      if (supportTicket) {
        collections.supportTicketsList.collection.update(
          supportTicketId,
          (c) => {
            c.numDrafts = Math.max(0, c.numDrafts + delta);
          },
        );
      }
    },
    [collections.supportTicketsList, supportTicketId],
  );

  const saveDraft = useCallback(
    async (input: SaveDraftInput) => {
      const isCreatingNewDraft = !existingDraftId;
      const savePromise = (async () => {
        setIsSavingDraft(true);
        try {
          if (existingDraftId) {
            const tx = collections.drafts.update(
              String(existingDraftId),
              (d) => {
                d.content = input.content;
                d.plainContent = input.plainContent ?? null;
                d.attachments = [...input.attachments];
              },
            );
            await tx.isPersisted.promise;
          } else {
            const tempItem = buildTempDraftItem(
              supportTicketId,
              input.threadId,
              senderMongoUserId,
              input.inReplyToMessageId,
              {
                content: input.content,
                plainContent: input.plainContent ?? null,
                attachments: [...input.attachments],
                channel: input.channel,
                channelData: input.channelData,
              },
            );
            const tx = collections.drafts.insert(tempItem, {
              optimistic: false,
            });
            await tx.isPersisted.promise;
          }
          if (isCreatingNewDraft) {
            incrementSupportTicketDrafts(1);
            invalidateSupportTicketCounts();
          }
        } finally {
          setIsSavingDraft(false);
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      return savePromise;
    },
    [
      collections.drafts,
      supportTicketId,
      existingDraftId,
      senderMongoUserId,
      incrementSupportTicketDrafts,
      invalidateSupportTicketCounts,
    ],
  );

  const deleteDraft = useCallback(
    async (draftId: DraftMessageId) => {
      setIsDeletingDraft(true);
      try {
        const tx = collections.drafts.delete(String(draftId));
        await tx.isPersisted.promise;
        toast("Draft deleted", { variant: "success" });
        incrementSupportTicketDrafts(-1);
        invalidateSupportTicketCounts();
      } catch {
        toast("Failed to delete draft", { variant: "error" });
      } finally {
        setIsDeletingDraft(false);
      }
    },
    [
      collections.drafts,
      incrementSupportTicketDrafts,
      invalidateSupportTicketCounts,
    ],
  );

  const waitForPendingSave = useCallback(async () => {
    if (pendingSaveRef.current) {
      await pendingSaveRef.current;
    }
  }, []);

  return {
    saveDraft,
    isSavingDraft,
    deleteDraft,
    isDeletingDraft,
    waitForPendingSave,
  };
}
